import { useCallback } from 'react';

import { EApiRoutes, getApiRouteOrMock } from '../api/api.utils';
import { useUserDetails } from '../api/useUserDetails';
import {
  EDashboardPage,
  EEventsType,
  ELocalStorageKeys,
  ELockoutSource
} from '../constants/enums';
import { localStorageUtil } from '../utils/localStorageUtil';

import useAxios from './useAxios';

export interface BigQueryEventData {
  eventName: EEventsType;
  publisherId?: string | null;
  userId?: string;
  userEmail?: string;
  countryCode?: string;
  annualRev?: string;
  teamSize?: string;
  websiteUrl?: string;
  isCheckboxApproved?: boolean;
  phone?: string;
  taskName?: string;
  taskStatus?: string;
  method?: string;
  template_id?: string;
  template_name?: string;
  is_active?: boolean;
  action?: string;
  role?: string;
  tab?: string;
  section?: string;
  companyId?: string;
  portalId?: string | null;
  businessName?: string;
  solution?: string;
  solutionSubtype?: string;
  page?: string;
  api?: string;
  version?: string;
  responseCode?: number;
  url?: string;
  status?: string;
}

export interface PreLoginDashboardEventData {
  page: EDashboardPage;
  eventName?: EEventsType;
  userEmail?: string;
  userId?: string;
  source?: ELockoutSource;
  cooldownTime?: string;
}

interface BaseEventPayload {
  publisherId?: string;
  userId?: string;
  userEmail?: string;
}

export const useBigQueryEventsTracking = () => {
  const axios = useAxios();
  const { userDetails } = useUserDetails();

  const getBaseEventData = useCallback(
    (overrides?: Partial<BaseEventPayload>): BaseEventPayload => {
      const publisherIdFromStorage = localStorageUtil.getAny<string>(
        ELocalStorageKeys.PUBLISHER_ID
      );

      const publisherId = overrides?.publisherId || publisherIdFromStorage;
      const userId = overrides?.userId || userDetails?.userId;
      const userEmail = overrides?.userEmail || userDetails?.email;

      return {
        ...(publisherId ? { publisherId } : {}),
        ...(userId ? { userId } : {}),
        ...(userEmail ? { userEmail } : {})
      };
    },
    [userDetails]
  );

  const trackBigQueryEvent = useCallback(
    async (eventData: BigQueryEventData) => {
      try {
        if (!eventData.eventName) {
          console.warn('Event name is required for BigQuery tracking');
          return;
        }

        const baseData = getBaseEventData({
          publisherId: eventData.publisherId ?? undefined,
          userId: eventData.userId,
          userEmail: eventData.userEmail
        });

        const payload = {
          ...baseData,
          eventName: eventData.eventName,
          ...(eventData.countryCode && { countryCode: eventData.countryCode }),
          ...(eventData.annualRev && { annualRev: eventData.annualRev }),
          ...(eventData.teamSize && { teamSize: eventData.teamSize }),
          ...(eventData.websiteUrl && { websiteUrl: eventData.websiteUrl }),
          ...(eventData.isCheckboxApproved !== undefined && {
            isCheckboxApproved: eventData.isCheckboxApproved
          }),
          ...(eventData.phone && { phone: eventData.phone }),
          ...(eventData.taskName && { taskName: eventData.taskName }),
          ...(eventData.taskStatus && { taskStatus: eventData.taskStatus }),
          ...(eventData.method && { method: eventData.method }),
          ...(eventData.template_id && {
            template_id: eventData.template_id
          }),
          ...(eventData.template_name && {
            template_name: eventData.template_name
          }),
          ...(eventData.action && {
            action: eventData.action
          }),
          ...(eventData.is_active && {
            is_active: eventData.is_active
          }),
          ...(eventData.role && { role: eventData.role }),
          ...(eventData.tab && { tab: eventData.tab }),
          ...(eventData.section && { section: eventData.section }),
          ...(eventData.companyId && { companyId: eventData.companyId }),
          ...(eventData.businessName && { businessName: eventData.businessName }),
          ...(eventData.solution && { solution: eventData.solution }),
          ...(eventData.solutionSubtype && { solutionSubtype: eventData.solutionSubtype }),
          ...(eventData.page && { page: eventData.page }),
          ...(eventData.api && { api: eventData.api }),
          ...(eventData.version && { version: eventData.version }),
          ...(eventData.responseCode !== undefined && { responseCode: eventData.responseCode }),
          ...(eventData.url && { url: eventData.url }),
          ...(eventData.status && { status: eventData.status }),
          portalId: eventData.portalId ?? null,
          publisherId: eventData.publisherId ?? null
        };

        await axios.post(
          getApiRouteOrMock(EApiRoutes.PUBLISHER_DASHBOARD_ACTIVITIES),
          payload
        );
      } catch (error) {
        console.error('Failed to send BigQuery event:', error);
      }
    },
    [axios, getBaseEventData]
  );

  const trackPreLoginEvent = useCallback(
    async (eventData: PreLoginDashboardEventData) => {
      try {
        if (!eventData.page) {
          console.warn('Page is required for pre-login tracking');
          return;
        }

        const baseData = getBaseEventData({
          userId: eventData.userId,
          userEmail: eventData.userEmail
        });

        const payload = {
          ...baseData,
          eventName:
            eventData.eventName ?? EEventsType.DASHBOARD_PAGE_DISPLAYED,
          page: eventData.page,
          ...(eventData.source && { source: eventData.source }),
          ...(eventData.cooldownTime !== undefined && {
            cooldownTime: eventData.cooldownTime
          })
        };

        await axios.post(
          getApiRouteOrMock(EApiRoutes.PRE_LOGIN_DASHBOARD_ACTIVITIES),
          payload
        );
      } catch (error) {
        console.error('Failed to send pre-login event:', error);
      }
    },
    [axios, getBaseEventData]
  );

  return {
    trackBigQueryEvent,
    trackPreLoginEvent
  };
};
